// EmptyState.tsx — designed empty-data state shared across lanes.
//
// Plan ref: 06-06-PLAN-cmd-k-toast-empty-loading-error.md §Task 2 step 3
//          + m03 Phase 4b (brand.md §4 — the empty state is the "inked-but-idle
//            press": the lane's glyph sits registered inside a large faint
//            registration crosshair, the print-shop mark. This is a *designed
//            state* motif, not chrome — the one sanctioned place the crosshair
//            gets to be decorative because the empty screen is where the brand
//            doc reserves identity).
//
// Layout — optically centered in the lane's remaining height (was a top-hugging
// py-12 block that left a dead void below; audit-critique #3):
//   ┌────────────────────────────────────┐
//   │                                    │
//   │        [ ✛ registration mark ]     │   ← lane icon inside a faint crosshair
//   │             title                  │
//   │           description              │
//   │              [CTA?]                │
//   │                                    │
//   └────────────────────────────────────┘
//
// a11y: role=status (polite live region — empty data is informational, not
// urgent). Reveal entry uses the canonical motion role from 06-01.

import { motion } from 'motion/react';
import type { LucideIcon } from '@/components/Icon';
import { useReveal } from '@/lib/motion';

interface EmptyStateCta {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Primary action — bordered, higher-emphasis. */
  cta?: EmptyStateCta;
  /**
   * Optional secondary action, rendered next to the primary CTA with a
   * lighter (ghost) treatment so the emphasis order is unambiguous. Used e.g.
   * by Today's empty queue to offer a forward "Today" reset alongside the
   * backward "Yesterday" primary.
   */
  secondaryCta?: EmptyStateCta;
}

/**
 * The press mark — the lane's glyph registered inside a large, faint
 * registration crosshair (same geometry as favicon.svg / RegisterChip). Reads
 * as "the block is inked and aligned; the press is idle, waiting on you."
 */
function PressMark({ icon: Icon }: { icon: LucideIcon }): JSX.Element {
  return (
    <div className="relative flex h-20 w-20 items-center justify-center">
      <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        fill="none"
        className="absolute inset-0 h-full w-full text-border-strong"
      >
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="0.75" />
        <rect x="0" y="7.7" width="16" height="0.6" fill="currentColor" />
        <rect x="7.7" y="0" width="0.6" height="16" fill="currentColor" />
      </svg>
      <Icon className="relative h-7 w-7 text-fg-subtle" strokeWidth={1.5} aria-hidden />
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  cta,
  secondaryCta,
}: EmptyStateProps): JSX.Element {
  const { variants, transition } = useReveal();
  return (
    // flex-1 fills the lane's remaining height; min-h gives presence even when
    // the parent doesn't constrain height. The inner block stays centered.
    <div className="flex min-h-[20rem] w-full flex-1 items-center justify-center px-6 py-10">
      <motion.div
        role="status"
        data-testid="empty-state"
        initial="hidden"
        animate="visible"
        variants={variants}
        transition={transition}
        className="flex max-w-md flex-col items-center text-center"
      >
        <PressMark icon={icon} />
        <h3 className="mt-4 text-base font-medium text-fg-default">{title}</h3>
        <p className="mt-2 max-w-xs text-sm text-fg-muted">{description}</p>
        {cta || secondaryCta ? (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {cta ? (
              <button
                type="button"
                onClick={cta.onClick}
                className="tap-target rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm font-medium text-fg-default hover:bg-overlay-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                {cta.label}
              </button>
            ) : null}
            {secondaryCta ? (
              <button
                type="button"
                onClick={secondaryCta.onClick}
                className="tap-target rounded-md px-3 py-1.5 text-sm font-medium text-fg-muted hover:bg-overlay-hover hover:text-fg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                {secondaryCta.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
